/**
 * Confirming a gallery conversion.
 *
 * Conversion swaps a generated gallery's frozen HTML for a section the
 * renderers drive, which is the only way photos can be added, removed or
 * reordered (ADR-009). It is a redesign of that section — the site's bespoke
 * layout for it is replaced by the standard grid — so this modal exists to make
 * that trade explicit rather than letting someone discover it afterwards.
 *
 * The `dropped` list is the important part of this screen. It is the content
 * the data model has nowhere to put, and it is shown before the button, not
 * after.
 */

import { useEffect, useState } from 'react';
import {
  previewGalleryConversion,
  convertGallery,
  type GalleryConversionPlan,
} from '../../api/editor';

interface ConvertGalleryModalProps {
  websiteId: string;
  pageSlug: string;
  sectionId: string;
  onClose: () => void;
  onConverted: () => void;
}

export function ConvertGalleryModal({
  websiteId,
  pageSlug,
  sectionId,
  onClose,
  onConverted,
}: ConvertGalleryModalProps) {
  const [plan, setPlan] = useState<GalleryConversionPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [converting, setConverting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    previewGalleryConversion(websiteId, pageSlug, sectionId)
      .then((res) => {
        if (!cancelled) setPlan(res.data.plan);
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message || 'Could not read this gallery.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [websiteId, pageSlug, sectionId]);

  const handleConvert = async () => {
    setConverting(true);
    setError(null);
    try {
      await convertGallery(websiteId, pageSlug, sectionId);
      onConverted();
      onClose();
    } catch (err) {
      setError((err as Error).message || 'Conversion failed.');
      setConverting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-gray-200">
          <h3 className="text-base font-semibold text-gray-900">Make this gallery editable</h3>
          <p className="text-xs text-gray-500 mt-1">
            Adds and removes photos, reorders them, and gives visitors a lightbox.
          </p>
        </div>

        <div className="px-5 py-4 overflow-y-auto flex-1">
          {loading && <p className="text-sm text-gray-500">Reading the gallery…</p>}

          {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {plan && !loading && (
            <div className="space-y-4">
              <div className="rounded-lg bg-gray-50 border border-gray-200 px-3 py-2.5">
                <p className="text-sm text-gray-800">
                  <span className="font-semibold">{plan.imageCount}</span> photo
                  {plan.imageCount === 1 ? '' : 's'} will carry across.
                </p>
                <ul className="mt-1.5 text-xs text-gray-600 space-y-0.5">
                  {plan.captionCount > 0 && <li>{plan.captionCount} keep their caption</li>}
                  {plan.framedCount > 0 && (
                    <li>{plan.framedCount} keep the framing you set by hand</li>
                  )}
                  {plan.categories.length > 0 && (
                    <li>Filter buttons: {plan.categories.join(', ')}</li>
                  )}
                </ul>
              </div>

              <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2.5">
                <p className="text-sm font-medium text-amber-900">
                  This section&rsquo;s layout will be replaced
                </p>
                <p className="text-xs text-amber-800 mt-1">
                  It becomes the standard photo grid. The rest of the page is untouched, and you
                  can revert to an earlier version at any time.
                </p>
              </div>

              {plan.dropped.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-900 mb-1.5">
                    This text has nowhere to go and will be lost:
                  </p>
                  <ul className="space-y-1">
                    {plan.dropped.map((d, i) => (
                      <li
                        key={i}
                        className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded px-2 py-1.5"
                      >
                        {d}
                      </li>
                    ))}
                  </ul>
                  <p className="text-xs text-gray-400 mt-1.5">
                    Copy anything you want to keep before converting.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button
            onClick={onClose}
            disabled={converting}
            className="px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleConvert}
            disabled={!plan || loading || converting}
            className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-40"
          >
            {converting ? 'Converting…' : 'Convert gallery'}
          </button>
        </div>
      </div>
    </div>
  );
}
